import { useEffect, useState } from 'react'
import { onPreferencesReset, readOnboardingChecklist, writeOnboardingChecklist } from './prefs'

const ONBOARDING_STEPS = ['sample', 'sources', 'settings'] as const
export type OnboardingStep = (typeof ONBOARDING_STEPS)[number]

export interface OnboardingChecklist {
  isDone: (step: OnboardingStep) => boolean
  toggle: (step: OnboardingStep) => void
  /** Steps not yet done, for the count beside Guide. */
  remaining: number
  total: number
}

/**
 * The getting-started checklist. A step ticks itself: while what it asks for
 * holds (`detected`: samples loaded, a source followed), or for good once the
 * person has done it (`reached`: Settings visited). A tick
 * or untick by hand wins over both. Held once, in the shell, so the guide
 * and the count beside its button agree.
 */
export function useOnboardingChecklist(
  detected: Partial<Record<OnboardingStep, boolean>>,
  reached: Partial<Record<OnboardingStep, boolean>> = {},
): OnboardingChecklist {
  const [ticked, setTicked] = useState(readOnboardingChecklist)
  // A reset of preferences clears the stored checklist; so too the one held here.
  useEffect(() => onPreferencesReset(() => setTicked(readOnboardingChecklist())), [])
  // Recorded once, during render, so the count never lags; a step ticked or
  // unticked by hand is left as it is.
  const newlyReached = ONBOARDING_STEPS.find((s) => reached[s] && !(s in ticked))
  if (newlyReached) {
    const next = { ...ticked, [newlyReached]: true }
    setTicked(next)
    writeOnboardingChecklist(next)
  }
  const isDone = (step: OnboardingStep) =>
    step in ticked ? Boolean(ticked[step]) : Boolean(detected[step])
  const toggle = (step: OnboardingStep) => {
    const next = { ...ticked, [step]: !isDone(step) }
    setTicked(next)
    writeOnboardingChecklist(next)
  }
  return {
    isDone,
    toggle,
    remaining: ONBOARDING_STEPS.filter((s) => !isDone(s)).length,
    total: ONBOARDING_STEPS.length,
  }
}
